import React, { forwardRef, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { mergeClasses } from '@/helpers/styling'
import { findOutermostPortalTarget } from '@/shared/portal-layers'

type Props = {
  target: HTMLElement
  variant: "hovered" | "selected"
}

/**
 * Render a nice outline around the widget to show as selection or selection candidate.
 *
 * This is rendered in a _portal_ with _absolute_ positioning (outside the regular widget DOM tree),
 * to avoid issues of being clipped by `overflow: hidden` and/or rounded border radiuses on parent elements.
 * It also avoids any issues with layout shifting and scrollbars.
 */
export const WidgetOutlineOverlay = forwardRef<
  HTMLDivElement, Props
>(function SelectionOverlay(props, ref) {
  const { target } = props
  const rect = useElementRect(target)

  if (!rect) return null
  const layer = widgetLayer(target)

  const label = props.variant === 'hovered' ? target.dataset.widgetType || "[Empty]" : target.dataset.widgetType

  return createPortal((
      <div
        ref={ref}
        className={mergeClasses(`
          position-absolute box-border z-[200]
          pointer-events-none cursor-default
          outline outline-2 outline-indigo-600
          font-exo text-xs
        `, {
          "opacity-100": props.variant === "selected"
        })}
        style={{
          top: rect.top - layer.origin.top,
          left: rect.left - layer.origin.left,
          width: rect.width,
          height: rect.height
        }}>
        {props.variant === 'hovered' && (
          <div className="position-absolute pointer-events-auto"
               style={{
                 left: 0,
                 top: Math.max(0, rect.visible.top - rect.top),
                 height: 'calc(24px + var(--layout-widget-select-helper-size))',
               }}>
            {/* Introduce extra invisible space overlapping the layout-widget-select-helper.
                This keeps the hover state continuous when moving the mouse to the label,
                instead of the parent layout widget taking over. */}
            <div className={TYPE_LABEL}
                 style={{ marginBottom: 'var(--layout-widget-select-helper-size)' }}>
              {label}
            </div>
          </div>
        )}
        {props.variant === 'selected' && label && (
          // in the top left corner, below the top bar when scrolled under it
          <div className={mergeClasses(TYPE_LABEL, "position-absolute left-0")}
               style={{ top: Math.max(0, rect.visible.top - rect.top) }}>
            {label}
          </div>
        )}
      </div>
    ),
    layer.container,
  )
})

/**
 * Where to portal UI around a widget: in its portal layer, so a dialog around the widget does not cover it.
 * `origin` is the layer's page position, to subtract from page coordinates when positioning in it.
 */
export function widgetLayer(target: HTMLElement) {
  const container = findOutermostPortalTarget(target)
  const origin = container === document.body ? { top: 0, left: 0 } : getRect(container)
  return { container, origin }
}

/** The widget type on the outline, the same while picking and once selected */
const TYPE_LABEL = "py-1 px-2 bg-indigo-600 opacity-90 text-white"

type Rect = ReturnType<typeof getRect>

/** Page coordinates of the area an element can show in */
export type VisibleBox = { top: number, left: number, right: number, bottom: number }

/** Its scroll containers' visible part, and the outermost one's: the app area an overlay may use */
type VisibleBoxes = VisibleBox & { app: VisibleBox }

/**
 * Page position of an element, for overlays rendered in a portal; null once it is detached.
 * `visible` is what its scroll containers leave in view, so an overlay can stay clear of the top bar.
 */
export function useElementRect(target: HTMLElement): (Rect & { visible: VisibleBoxes }) | null {
  const [ rect, setRect ] = useState<(Rect & { visible: VisibleBoxes }) | null>(null)

  useLayoutEffect(function trackTargetRect() {
    const update = () => setRect(target.isConnected ? { ...getRect(target), visible: getVisibleBox(target) } : null)

    update()

    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)

    const ro = new ResizeObserver(update)
    ro.observe(target)

    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
      ro.disconnect()
    }
  }, [ target ])

  return rect
}

function getRect(el: HTMLElement) {
  const r = el.getBoundingClientRect()
  return {
    top: snap(r.top + window.scrollY),
    left: snap(r.left + window.scrollX),
    width: snap(r.width),
    height: snap(r.height),
  } as const
}

function getVisibleBox(el: HTMLElement): VisibleBoxes {
  const page = {
    top: window.scrollY,
    left: window.scrollX,
    right: window.scrollX + window.innerWidth,
    bottom: window.scrollY + window.innerHeight,
  }
  const box = { ...page }
  let app = { ...page }
  for (let ancestor = el.parentElement; ancestor && ancestor !== document.body; ancestor = ancestor.parentElement) {
    const style = getComputedStyle(ancestor)
    if (style.overflowX === 'visible' && style.overflowY === 'visible') continue
    const r = ancestor.getBoundingClientRect()
    const clip = {
      top: Math.max(page.top, r.top + window.scrollY),
      left: Math.max(page.left, r.left + window.scrollX),
      right: Math.min(page.right, r.right + window.scrollX),
      bottom: Math.min(page.bottom, r.bottom + window.scrollY),
    }
    box.top = Math.max(box.top, clip.top)
    box.left = Math.max(box.left, clip.left)
    box.right = Math.min(box.right, clip.right)
    box.bottom = Math.min(box.bottom, clip.bottom)
    app = clip
  }
  return { ...box, app }
}

/** Snap for pixel-perfect alignment with label, as getBoundingClientRect() returns fractional numbers */
function snap(value: number) {
  const dpr = window.devicePixelRatio || 1
  return Math.round(value * dpr) / dpr
}
